import React, { useEffect, useMemo, useState } from 'react';
import { bookingsAPI, publicAPI } from './services/api';
import PaymentModal from './PaymentModal';
import SalesAgreement from './SalesAgreement';
import { validatePhone, formatPhoneDisplay } from './utils/phoneUtils';
import './SunbedBooking.css';

interface Zone {
  id: number;
  row_number: number;
  zone_number: number;
  label: string;
  sunbed_count: number;
  weekday_price: number;
  weekend_price: number;
  weekday_extra_person_fee: number;
  weekend_extra_person_fee: number;
  min_persons: number;
  max_persons: number;
  active: boolean;
  sort_order: number;
}

interface GeneralCfg {
  weekend_days: number[];
  min_persons_per_zone: number;
  max_persons_per_zone: number;
  prices_include_vat: boolean;
  require_adjacent_zones: boolean;
}

interface SelectedZone {
  zone_id: number;
  person_count: number;
}

// Krokideki sıra → SVG geometrisi (müşterinin krokisine birebir uyumlu)
// viewBox: 0..1000 × 0..720
const ROW_LAYOUTS: Record<number, { x: number; y: number; width: number; height: number }> = {
  1: { x: 110, y: 110, width: 240, height: 55 }, // 1.Sıra - sol üst, deniz altında, iskele solunda (4 bölge × 60)
  2: { x: 425, y: 240, width: 420, height: 50 }, // 2.Sıra - 6 bölge × 70
  3: { x: 425, y: 295, width: 350, height: 50 }, // 3.Sıra - 5 bölge × 70
  4: { x: 425, y: 385, width: 280, height: 50 }, // 4.Sıra - 4 bölge × 70
  5: { x: 425, y: 440, width: 280, height: 50 }, // 5.Sıra
  6: { x: 425, y: 495, width: 280, height: 50 }, // 6.Sıra
  7: { x: 425, y: 550, width: 280, height: 50 }  // 7.Sıra
};

const SunbedBooking: React.FC = () => {
  const [selectedDate, setSelectedDate] = useState<string>('');
  const [blockedDates, setBlockedDates] = useState<{ date: string; reason: string; affects: string[] }[]>([]);
  const [blockedDateError, setBlockedDateError] = useState<string>('');

  const [zones, setZones] = useState<Zone[]>([]);
  const [general, setGeneral] = useState<GeneralCfg>({
    weekend_days: [0, 6],
    min_persons_per_zone: 2,
    max_persons_per_zone: 3,
    prices_include_vat: true,
    require_adjacent_zones: false
  });
  const [occupiedZoneIds, setOccupiedZoneIds] = useState<Set<number>>(new Set());
  const [selected, setSelected] = useState<SelectedZone[]>([]);
  const [hoverZoneId, setHoverZoneId] = useState<number | null>(null);

  const [customerName, setCustomerName] = useState('');
  const [customerEmail, setCustomerEmail] = useState('');
  const [customerPhone, setCustomerPhone] = useState('');
  const [arrivalTime, setArrivalTime] = useState('');
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [agreementAccepted, setAgreementAccepted] = useState(false);

  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentUrl, setPaymentUrl] = useState('');
  const [createdBookingId, setCreatedBookingId] = useState('');
  const [loading, setLoading] = useState(false);

  const [sunbedFeaturesHTML, setSunbedFeaturesHTML] = useState('');

  // ----- Veri çekme -----
  useEffect(() => {
    publicAPI.getBlockedDates()
      .then(res => { if (res.data.success) setBlockedDates(res.data.data); })
      .catch(() => {});

    fetch('/api/products/sunbed/zones')
      .then(r => r.json())
      .then(d => {
        if (d.success) {
          setZones(d.data.zones || []);
          if (d.data.general) setGeneral({
            weekend_days: d.data.general.weekend_days || [0, 6],
            min_persons_per_zone: d.data.general.min_persons_per_zone || 2,
            max_persons_per_zone: d.data.general.max_persons_per_zone || 3,
            prices_include_vat: d.data.general.prices_include_vat !== false,
            require_adjacent_zones: d.data.general.require_adjacent_zones === true
          });
          if (d.data.features_html != null) setSunbedFeaturesHTML(d.data.features_html);
        }
      })
      .catch(err => console.error('Bölgeler alınamadı:', err));
  }, []);

  useEffect(() => {
    if (!selectedDate) {
      setOccupiedZoneIds(new Set());
      return;
    }
    fetch(`/api/products/sunbed/zones/availability/${selectedDate}`)
      .then(r => r.json())
      .then(d => {
        if (d.success) {
          setOccupiedZoneIds(new Set((d.data.occupied || []).map((o: any) => o.zone_id)));
        }
      })
      .catch(() => {});
    // Tarih değişince seçimi sıfırla
    setSelected([]);
  }, [selectedDate]);

  // ----- Helper -----
  const isWeekend = useMemo(() => {
    if (!selectedDate) return false;
    const d = new Date(selectedDate);
    return general.weekend_days.includes(d.getUTCDay());
  }, [selectedDate, general]);

  const isDateBlocked = (dateStr: string) =>
    blockedDates.some(e => e.date === dateStr && (e.affects.includes('all') || e.affects.includes('sunbed')));
  const getBlockedReason = (dateStr: string) => {
    const e = blockedDates.find(entry => entry.date === dateStr && (entry.affects.includes('all') || entry.affects.includes('sunbed')));
    return e?.reason || '';
  };

  const zonePrice = (zone: Zone, persons: number) => {
    const base = isWeekend ? Number(zone.weekend_price) : Number(zone.weekday_price);
    const extraFee = isWeekend ? Number(zone.weekend_extra_person_fee) : Number(zone.weekday_extra_person_fee);
    const extras = Math.max(0, persons - (zone.min_persons || 2));
    return base + extras * (extraFee || 0);
  };

  const totalPrice = useMemo(() => {
    return selected.reduce((sum, s) => {
      const z = zones.find(zz => zz.id === s.zone_id);
      if (!z) return sum;
      return sum + zonePrice(z, s.person_count);
    }, 0);
  }, [selected, zones, isWeekend]);

  const totalPersons = useMemo(
    () => selected.reduce((s, x) => s + x.person_count, 0),
    [selected]
  );

  // Çoklu bölge için kısıtlama yok — müşteri istediği bölgeleri seçebilir
  const multiZoneWarning = useMemo<string | null>(() => null, []);

  const toggleZone = (zone: Zone) => {
    if (!selectedDate) {
      alert('Lütfen önce bir tarih seçin!');
      return;
    }
    if (occupiedZoneIds.has(zone.id)) return;

    const isSel = selected.some(s => s.zone_id === zone.id);
    if (isSel) {
      setSelected(prev => prev.filter(s => s.zone_id !== zone.id));
    } else {
      // Her bölge varsayılan 2 kişi; 3. kişi ekleme bölge seçildikten sonra checkbox ile yapılır
      setSelected(prev => [...prev, { zone_id: zone.id, person_count: zone.min_persons || 2 }]);
    }
  };

  const toggleThirdPerson = (zoneId: number) => {
    setSelected(prev => prev.map(s => {
      if (s.zone_id !== zoneId) return s;
      const z = zones.find(zz => zz.id === zoneId);
      const minP = z?.min_persons || 2;
      const maxP = z?.max_persons || 3;
      return { ...s, person_count: s.person_count >= maxP ? minP : maxP };
    }));
  };

  // ----- SVG kroki üretimi -----
  const zonesByRow = useMemo(() => {
    const map = new Map<number, Zone[]>();
    zones.forEach(z => {
      if (!z.active) return;
      const arr = map.get(z.row_number) || [];
      arr.push(z);
      map.set(z.row_number, arr);
    });
    map.forEach((arr) => arr.sort((a, b) => a.zone_number - b.zone_number));
    return map;
  }, [zones]);

  const renderZoneRects = () => {
    const rects: React.ReactElement[] = [];
    zonesByRow.forEach((rowZones, rowNum) => {
      const layout = ROW_LAYOUTS[rowNum];
      if (!layout) return;
      const count = rowZones.length;
      const gap = 4;
      const w = (layout.width - gap * (count - 1)) / count;
      rowZones.forEach((z, idx) => {
        const x = layout.x + idx * (w + gap);
        const isOcc = occupiedZoneIds.has(z.id);
        const sel = selected.some(s => s.zone_id === z.id);
        const fill = isOcc ? '#e74c3c' : sel ? '#2196f3' : '#a5d6a7';
        const stroke = sel ? '#0d47a1' : '#333';
        const price = isWeekend ? z.weekend_price : z.weekday_price;
        rects.push(
          <g
            key={z.id}
            className={`zone ${isOcc ? 'occupied' : sel ? 'selected' : 'available'}`}
            onClick={() => toggleZone(z)}
            onMouseEnter={() => setHoverZoneId(z.id)}
            onMouseLeave={() => setHoverZoneId(null)}
            style={{ cursor: isOcc ? 'not-allowed' : 'pointer' }}
          >
            <rect
              x={x}
              y={layout.y}
              width={w}
              height={layout.height}
              fill={fill}
              stroke={stroke}
              strokeWidth={sel ? 2.5 : 1}
              rx={6}
            />
            <text
              x={x + w / 2}
              y={layout.y + layout.height / 2 - 9}
              textAnchor="middle"
              fontSize={11}
              fontWeight="700"
              fill="#222"
            >
              {z.zone_number}
            </text>
            <text
              x={x + w / 2}
              y={layout.y + layout.height / 2 + 4}
              textAnchor="middle"
              fontSize={10}
              fill="#222"
            >
              ₺{Number(price).toLocaleString('tr-TR')}
            </text>
            <text
              x={x + w / 2}
              y={layout.y + layout.height / 2 + 16}
              textAnchor="middle"
              fontSize={9}
              fill="#555"
            >
              👤 max {z.max_persons || 3}
            </text>
          </g>
        );
      });
    });
    return rects;
  };

  const hoverZone = hoverZoneId ? zones.find(z => z.id === hoverZoneId) : null;

  // ----- Submit -----
  const handleBooking = async () => {
    if (selected.length === 0) { alert('Lütfen en az bir bölge seçin!'); return; }
    if (!selectedDate) { alert('Lütfen bir tarih seçin!'); return; }
    if (isDateBlocked(selectedDate)) {
      const reason = getBlockedReason(selectedDate);
      alert(`Bu tarihte şezlong rezervasyonu yapılamamaktadır${reason ? ` (${reason})` : ''}.`);
      return;
    }
    if (multiZoneWarning) {
      alert(multiZoneWarning);
      return;
    }
    if (!customerName.trim()) { alert('Lütfen adınızı ve soyadınızı girin!'); return; }
    if (!customerEmail.trim()) { alert('Lütfen e-posta adresinizi girin!'); return; }
    const pv = validatePhone(customerPhone);
    if (!pv.valid) { alert(`Telefon numarası hatalı: ${pv.error}`); return; }
    if (!arrivalTime) { alert('Lütfen geliş saatinizi seçin!'); return; }
    if (!termsAccepted) { alert('Lütfen koşulları kabul edin!'); return; }
    if (!agreementAccepted) { alert('Lütfen satış sözleşmesini onaylayın!'); return; }

    setLoading(true);
    try {
      const zoneDetails = selected.map(s => {
        const z = zones.find(zz => zz.id === s.zone_id)!;
        return {
          zone_id: s.zone_id,
          row_number: z.row_number,
          zone_number: z.zone_number,
          label: z.label,
          person_count: s.person_count,
          price: zonePrice(z, s.person_count)
        };
      });

      const bookingData = {
        product_type: 'sunbed',
        booking_date: selectedDate,
        customer_name: customerName,
        customer_email: customerEmail,
        customer_phone: pv.formatted,
        customer_info: { arrivalTime, zoneDetails, isWeekend },
        items: {
          zones: selected.map(s => ({ zone_id: s.zone_id, person_count: s.person_count })),
          zoneCount: selected.length,
          totalPersons
        },
        total_price: totalPrice,
        notes: ''
      };

      const response = await bookingsAPI.create(bookingData);
      if (response.data.success) {
        const { iframeUrl, reservationId } = response.data.data;
        setPaymentUrl(iframeUrl);
        setCreatedBookingId(reservationId);
        setShowPaymentModal(true);
      }
    } catch (err: any) {
      console.error('Rezervasyon hatası:', err);
      alert('Rezervasyon oluşturulamadı: ' + (err.response?.data?.message || err.message));
    } finally {
      setLoading(false);
    }
  };

  const getTodayDate = () => new Date().toISOString().split('T')[0];

  return (
    <div className="sunbed-booking">
      <div className="sunbed-container">
        {/* Sol panel */}
        <div className="info-panel">
          <div className="info-card">
            <h2>📌 Plaj Krokisi</h2>
            <p>Krokiden bir <strong>bölge</strong> seçin. Her bölge 2 şezlongdan oluşur. 3. kişi eklenebilir.</p>
            <p style={{ fontSize: '0.85rem', color: '#555' }}>
              {isWeekend ? '📅 Bu tarih hafta sonu fiyatlandırması' : '📅 Bu tarih hafta içi fiyatlandırması'}
            </p>
          </div>

          <div className="info-card">
            <h2>✅ Şezlong Özellikleri</h2>
            {sunbedFeaturesHTML ? (
              <div className="menu-preview-content features-content" dangerouslySetInnerHTML={{ __html: sunbedFeaturesHTML }} />
            ) : (
              <ul className="features-list">
                <li>🌞 Şemsiye dahil</li>
                <li>🧸 Havlu servisi</li>
                <li>🧳 Soyunma kabini erişimi</li>
                <li>🚿 Duş imkanı</li>
                <li>🍹 Beach bar yakınında</li>
              </ul>
            )}
          </div>

          <div className="legend">
            <div className="legend-item"><span className="legend-box available" /><span>Müsait</span></div>
            <div className="legend-item"><span className="legend-box selected" /><span>Seçili</span></div>
            <div className="legend-item"><span className="legend-box occupied" /><span>Dolu</span></div>
          </div>

          {hoverZone && (
            <div className="info-card" style={{ background: '#fffde7' }}>
              <h2>🔍 {hoverZone.label}</h2>
              <p>Kapasite: 2 kişi (3. kişi için ek ücret)</p>
              <p>Hafta içi: <strong>₺{Number(hoverZone.weekday_price).toLocaleString('tr-TR')}</strong></p>
              <p>Hafta sonu: <strong>₺{Number(hoverZone.weekend_price).toLocaleString('tr-TR')}</strong></p>
              <p style={{ fontSize: '0.85rem' }}>
                Ek kişi (3. kişi): hafta içi ₺{Number(hoverZone.weekday_extra_person_fee).toLocaleString('tr-TR')} / hafta sonu ₺{Number(hoverZone.weekend_extra_person_fee).toLocaleString('tr-TR')}
              </p>
            </div>
          )}
        </div>

        {/* Sağ panel */}
        <div className="booking-panel">
          <div className="booking-card">
            <h2>Rezervasyon Yap</h2>

            {/* Tarih */}
            <div className="form-group">
              <label>📅 Tarih Seçin</label>
              <input
                type="date"
                min={getTodayDate()}
                value={selectedDate}
                onChange={(e) => {
                  const val = e.target.value;
                  setSelectedDate(val);
                  if (val && isDateBlocked(val)) {
                    const reason = getBlockedReason(val);
                    setBlockedDateError(`Bu tarihte şezlong rezervasyonu yapılamamaktadır${reason ? ` (${reason})` : ''}.`);
                  } else {
                    setBlockedDateError('');
                  }
                }}
                className="date-select"
              />
              {blockedDateError && (
                <div className="warning-box">⚠️ {blockedDateError}</div>
              )}
            </div>

            {/* Bilgi notu */}
            <div style={{
              padding: '0.7rem 0.9rem', marginBottom: '1rem',
              background: '#e3f2fd', border: '1px solid #90caf9',
              borderRadius: 6, fontSize: '0.88rem', color: '#0d47a1'
            }}>
              ℹ️ Her bölge <strong>2 kişiliktir</strong>. İhtiyaç halinde <strong>3. kişi için ek şezlong</strong> eklenebilir (ek ücret).
            </div>

            {/* SVG plaj krokisi */}
            <div className="beach-map-wrapper">
              <svg viewBox="0 0 1000 720" className="beach-svg" preserveAspectRatio="xMidYMid meet">
                {/* Deniz - üst geniş */}
                <rect x={110} y={30} width={860} height={70} fill="#4dd0e1" stroke="#00838f" strokeWidth={2} />
                <text x={540} y={72} textAnchor="middle" fontSize={22} fontWeight="700" fill="#003">Deniz</text>

                {/* 1.Sıra zone'ları yan etiketi kaldırıldı — diğer sıralar gibi sadece "1.Sıra" yazısı (aşağıda map'te) */}

                {/* İskele - 1.Sıra hizasında dikey gri şerit */}
                <rect x={355} y={110} width={25} height={55} fill="#cfd8dc" stroke="#333" />
                <text x={368} y={138} textAnchor="middle" fontSize={11} fontWeight="700" fill="#333" transform="rotate(-90 368 138)">İskele</text>

                {/* Konaklama Şezlong Alanı - İskele'nin sağında, deniz altında */}
                <rect x={385} y={110} width={585} height={55} fill="#ffb74d" stroke="#333" strokeWidth={1.5} />
                <text x={678} y={143} textAnchor="middle" fontSize={13} fontWeight="700" fill="#222">Konaklama Şezlong Alanı</text>

                {/* Yürüyüş yolu - SADECE sağ tarafta, Konaklama'nın altında */}
                <rect x={385} y={170} width={585} height={22} fill="#e0e0e0" stroke="#999" />
                <text x={678} y={186} textAnchor="middle" fontSize={11} fontWeight="600" fill="#444">Yürüyüş yolu</text>

                {/* Kapalı Restoran - 1.Sıra zone'larının altında, biraz daha geniş (İskele'yi de kapsar) */}
                <rect x={110} y={200} width={270} height={55} fill="#ef5350" stroke="#333" strokeWidth={1.5} />
                <text x={245} y={233} textAnchor="middle" fontSize={13} fontWeight="700" fill="#fff">Kapalı Restoran</text>

                {/* Yürüyüş yolu - DİKEY, sol tarafta: sol kolonu (Restoran/1.Sıra) ile 2-7.Sıra arasında ayırır */}
                <rect x={385} y={200} width={30} height={395} fill="#e0e0e0" stroke="#999" />
                <text x={400} y={400} textAnchor="middle" fontSize={11} fontWeight="600" fill="#444" transform="rotate(-90 400 400)">Yürüyüş yolu</text>

                {/* Yürüyüş yolu - 3.Sıra ile 4.Sıra arası yatay */}
                <rect x={425} y={355} width={420} height={20} fill="#e0e0e0" stroke="#999" />
                <text x={635} y={369} textAnchor="middle" fontSize={10} fill="#333">Yürüyüş yolu</text>

                {/* Bar - 2-3.Sıra hizasında, sağ kenarda */}
                <rect x={855} y={240} width={85} height={115} fill="#7e57c2" stroke="#333" strokeWidth={1.5} />
                <text x={898} y={302} textAnchor="middle" fontSize={16} fontWeight="700" fill="#fff">Bar</text>

                {/* Yürüyüş yolu - 4-7.Sıra sağında dikey */}
                <rect x={715} y={385} width={30} height={215} fill="#e0e0e0" stroke="#999" />
                <text x={730} y={492} textAnchor="middle" fontSize={10} fill="#555" transform="rotate(-90 730 492)">Yürüyüş yolu</text>

                {/* Çim Alan - alt */}
                <rect x={425} y={615} width={420} height={90} fill="#aed581" stroke="#33691e" strokeWidth={1.5} />
                <text x={635} y={668} textAnchor="middle" fontSize={18} fontWeight="700" fill="#1b5e20">Çim Alan</text>

                {/* Sıra etiketleri (her bölge satırının solunda) */}
                {Object.entries(ROW_LAYOUTS).map(([rn, l]) => (
                  <text
                    key={`label-${rn}`}
                    x={l.x - 8}
                    y={l.y + l.height / 2 + 4}
                    textAnchor="end"
                    fontSize={12}
                    fontWeight="700"
                    fill="#444"
                  >
                    {rn}.Sıra
                  </text>
                ))}

                {/* Tıklanabilir bölgeler */}
                {renderZoneRects()}
              </svg>
            </div>

            {/* Seçili bölgeler için kişi sayısı */}
            {selected.length > 0 && (
              <div className="zone-selection-list">
                <h3>👥 Seçili Bölgeler ve Kişi Sayısı</h3>
                {selected.map(s => {
                  const z = zones.find(zz => zz.id === s.zone_id);
                  if (!z) return null;
                  const extraFee = isWeekend ? Number(z.weekend_extra_person_fee) : Number(z.weekday_extra_person_fee);
                  const hasThird = s.person_count >= (z.max_persons || 3);
                  return (
                    <div key={s.zone_id} className="zone-row">
                      <div className="zone-row-info">
                        <strong>{z.label}</strong>
                        <span> · {s.person_count} kişi · {isWeekend ? 'Hafta sonu' : 'Hafta içi'}</span>
                      </div>
                      <div className="zone-row-controls">
                        <label style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', cursor: 'pointer', fontSize: '0.9rem' }}>
                          <input
                            type="checkbox"
                            checked={hasThird}
                            onChange={() => toggleThirdPerson(s.zone_id)}
                          />
                          ➕ 3. kişi ekle (+₺{extraFee.toLocaleString('tr-TR')})
                        </label>
                        <strong className="zone-price">₺{zonePrice(z, s.person_count).toLocaleString('tr-TR')}</strong>
                        <button className="btn-remove" onClick={() => toggleZone(z)}>✕</button>
                      </div>
                    </div>
                  );
                })}

                {multiZoneWarning && (
                  <div className="warning-box">⚠️ {multiZoneWarning}</div>
                )}
              </div>
            )}

            {/* Müşteri formu */}
            {selected.length > 0 && (
              <div className="customer-info-form">
                <h3>👤 İletişim Bilgileri</h3>
                <div className="form-group">
                  <label>Ad Soyad *</label>
                  <input type="text" value={customerName} onChange={e => setCustomerName(e.target.value)} placeholder="Adınız ve Soyadınız" className="input-field" />
                </div>
                <div className="form-group">
                  <label>E-posta Adresi *</label>
                  <input type="email" value={customerEmail} onChange={e => setCustomerEmail(e.target.value)} placeholder="ornek@email.com" className="input-field" />
                </div>
                <div className="form-group">
                  <label>Telefon Numarası *</label>
                  <input
                    type="tel"
                    value={customerPhone}
                    onChange={e => setCustomerPhone(e.target.value.replace(/[^0-9+\s]/g, ''))}
                    onBlur={() => { const v = validatePhone(customerPhone); if (v.valid) setCustomerPhone(formatPhoneDisplay(customerPhone)); }}
                    placeholder="05XX XXX XX XX"
                    maxLength={20}
                    className="input-field"
                  />
                  {customerPhone && !validatePhone(customerPhone).valid && (
                    <small style={{ color: '#e74c3c' }}>⚠️ {validatePhone(customerPhone).error}</small>
                  )}
                </div>
                <div className="form-group">
                  <label>🕒 Geliş Saati *</label>
                  <input type="time" value={arrivalTime} onChange={e => setArrivalTime(e.target.value)} className="input-field" />
                </div>
              </div>
            )}

            {/* Koşullar */}
            {selected.length > 0 && (
              <div className="warning-box" style={{ marginTop: '1rem' }}>
                <p style={{ margin: 0 }}>
                  ⚠️ <strong>Villa Polikne Yönetimi</strong>, şezlong rezervasyonu için giriş esnasında kadın/erkek dengesi ve kapasite durumuna göre müşteriyi kabul etmeme hakkını saklı tutar.
                </p>
                <div style={{ marginTop: '0.5rem', display: 'flex', alignItems: 'center' }}>
                  <input type="checkbox" id="termsCheckbox" checked={termsAccepted} onChange={e => setTermsAccepted(e.target.checked)} style={{ marginRight: '0.5rem' }} />
                  <label htmlFor="termsCheckbox" style={{ cursor: 'pointer', fontWeight: 600 }}>Koşulları okudum ve kabul ediyorum</label>
                </div>
              </div>
            )}

            {/* Özet */}
            {selected.length > 0 && (
              <div className="booking-summary-box">
                <div className="summary-row"><span>Seçili Bölge:</span><span className="summary-value">{selected.length}</span></div>
                <div className="summary-row"><span>Toplam Kişi:</span><span className="summary-value">{totalPersons}</span></div>
                <div className="summary-row"><span>Tarif:</span><span className="summary-value">{isWeekend ? 'Hafta sonu' : 'Hafta içi'}</span></div>
                <div className="summary-row"><span>Tarih:</span><span className="summary-value">{selectedDate || '-'}</span></div>
                <div className="summary-row total-row">
                  <span>Toplam (KDV dahil):</span>
                  <span className="summary-price">₺{totalPrice.toLocaleString('tr-TR')}</span>
                </div>
              </div>
            )}

            {selected.length > 0 && (
              <div style={{ marginTop: '1rem' }}>
                <SalesAgreement isChecked={agreementAccepted} onCheckChange={setAgreementAccepted} />
              </div>
            )}

            <button
              className="btn-sunbed-book"
              onClick={handleBooking}
              disabled={
                loading || !selectedDate || selected.length === 0 ||
                !customerName.trim() || !customerEmail.trim() || !customerPhone.trim() ||
                !arrivalTime || !termsAccepted || !agreementAccepted ||
                !!multiZoneWarning
              }
            >
              {loading ? '⏳ İşleniyor...' : '💳 Ödeme Sayfasına Geç'}
            </button>

            {/* Eksik alanlar ipucu - sadece bir şey eksikse göster */}
            {(() => {
              const missing: string[] = [];
              if (!selectedDate) missing.push('Tarih');
              if (selected.length === 0) missing.push('Bölge seçimi');
              if (!customerName.trim()) missing.push('Ad Soyad');
              if (!customerEmail.trim()) missing.push('E-posta');
              if (!customerPhone.trim()) missing.push('Telefon');
              if (!arrivalTime) missing.push('Geliş saati');
              if (!termsAccepted) missing.push('Koşulları kabul');
              if (!agreementAccepted) missing.push('Satış sözleşmesi onayı');
              if (multiZoneWarning) missing.push('Bölge uyumu');
              if (missing.length === 0 || loading) return null;
              return (
                <div style={{ marginTop: '0.5rem', fontSize: '0.85rem', color: '#b71c1c', background: '#ffebee', padding: '0.5rem 0.75rem', borderRadius: 6 }}>
                  ⚠️ Ödeme butonu aktif olması için eksik: <strong>{missing.join(', ')}</strong>
                </div>
              );
            })()}
          </div>
        </div>
      </div>

      <PaymentModal
        isOpen={showPaymentModal}
        onClose={async () => {
          setShowPaymentModal(false);
          // Müşteri ödemeden vazgeçtiyse pending rezervasyonu iptal et
          // ve şezlong bölge hold'unu serbest bırak
          if (createdBookingId) {
            try {
              await bookingsAPI.cancelPending(createdBookingId);
            } catch (e) {
              console.error('cancel-pending error:', e);
            }
            setCreatedBookingId('');
            setPaymentUrl('');
            // Bölge müsaitlik listesini yenile (iptal edilen bölge tekrar seçilebilsin)
            if (selectedDate) {
              try {
                const r = await fetch(`/api/products/sunbed/zones/availability/${selectedDate}`);
                const d = await r.json();
                if (d.success) {
                  setOccupiedZoneIds(new Set((d.data.occupied || []).map((o: any) => o.zone_id)));
                }
              } catch {}
            }
          }
        }}
        iframeUrl={paymentUrl}
        bookingId={createdBookingId}
        skipAgreement={true}
      />
    </div>
  );
};

export default SunbedBooking;
